import Highlight from '../components/Highlight';
import Heading from '../components/Heading';

const Mutations = () => {
  return (
    <div>
      <p>
        Use mutations to change your current state's values. Mutations are synchronous operations and can be called with
        the <code>useMutation()</code> hook or inside an action.
      </p>
      <Highlight language="javascript">
        {indexCode}
      </Highlight>

      <hr />

      <Heading>useMutation</Heading>
      <h3 className="heading-types">
        useMutation(mutationName: string) => (args?: any) => void
      </h3>
      <p>
        To use a mutation in a component, use the <code>useMutation()</code> hook.
      </p>
      <Highlight language="javascript">
        {componentCode}
      </Highlight>
      <p>Mutations don't get re-created, so you're safe with using them as dependencies
        for <code>useEffect</code> or <code>useCallback</code>.</p>
      <Highlight language="javascript">
        {dependencyCode}
      </Highlight>

      <hr />

      <Heading>useMutations</Heading>
      <h3 className="heading-types">
        useMutations(values: string[]) => ((args?: any) => void)[]
      </h3>
      <p>
        If you wish to use more mutations in a component, you can use the <code>useMutations()</code> hook instead of
        using useMutation() multiple times. The returned functions will be in the same order as provided arguments.
      </p>
      <Highlight language="javascript">
        {componentCode2}
      </Highlight>

      <hr />

      <Heading>useMutation with modules</Heading>

      <p>
        To access a module's mutation, use the module name as the prefix.
        <br />
        <code>const mutation = useMutation('counter/COUNTER_SET')</code>
      </p>
      <Highlight language="javascript">
        {modulesCode}
      </Highlight>
    </div>
  )
}

const indexCode = `const store = {
  state: {
    posts: []
  },
  mutations: {
    POSTS_SET (state, data) {
      state.posts = data
    },
    POST_ADD (state, data) {
      state.posts.unshift(data)
    },
    POST_REMOVE (state, id) {
      state.posts = state.posts.filter(p => p.id !== id)
    }
  }
}
`

const componentCode = `import { useMutation } from 'vuex-but-for-react'

const PostForm = () => {
  const onAdd = useMutation('POST_ADD');
  
  const handlePostAdd = () => {
    onAdd({ id: 1, title: 'New post to be added', body: 'Awesome text inside' })
  }
  
  return (
    <button onClick={handlePostAdd}>Add new post</button>
  )
}`

const dependencyCode = `const onSet = useMutation('POST_SET');

useEffect(() => {
  async function fetchData() {
    const data = await someApiCall();
    onSet(data);
  }
  fetchData();
}, [onSet])
`

const componentCode2 = `const [onAdd, onRemove] = useMutations(['POST_ADD', 'POST_REMOVE']);`

const modulesCode = `const store = {
  state: {
    ...
  },
  mutations: {
    ...
  },
  modules: {
    counter: {
      state: { count: 0 },
      mutations: {
        COUNTER_SET (state, newValue) {
          // a module does not have access to the parent state
          state.count = newValue
        }
      }
    }
  }
}
`

export default Mutations;
